import * as React from "react";
import { cn } from "@/lib/utils";

export type DotTone = "success" | "warning" | "danger" | "info" | "neutral" | "brand";

const TONE_BG: Record<DotTone, string> = {
  success: "bg-rd-success-500",
  warning: "bg-rd-warning-500",
  danger: "bg-rd-danger-500",
  info: "bg-rd-info-500",
  brand: "bg-brand-500",
  neutral: "bg-text-tertiary",
};

const HALO_STYLE: Record<DotTone, React.CSSProperties> = {
  success: { boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.18)" },
  warning: { boxShadow: "0 0 0 3px rgba(245, 158, 11, 0.18)" },
  danger: { boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.18)" },
  info: { boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.18)" },
  brand: { boxShadow: "0 0 0 3px rgba(139, 92, 246, 0.18)" },
  neutral: { boxShadow: "0 0 0 3px rgba(156, 163, 175, 0.18)" },
};

export interface DotProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: DotTone;
  halo?: boolean;
  pulse?: boolean;
  size?: number;
}

export const Dot = React.forwardRef<HTMLSpanElement, DotProps>(
  ({ tone = "neutral", halo = true, pulse = false, size = 8, className, style, ...rest }, ref) => {
    return (
      <span
        ref={ref}
        aria-hidden="true"
        data-tone={tone}
        className={cn(
          "inline-block h-[var(--dot-size)] w-[var(--dot-size)] shrink-0 rounded-full",
          TONE_BG[tone],
          pulse && "motion-safe:animate-pulse-dot",
          className
        )}
        style={
          {
            "--dot-size": `${size}px`,
            ...(halo ? HALO_STYLE[tone] : {}),
            ...style,
          } as React.CSSProperties
        }
        {...rest}
      />
    );
  }
);
Dot.displayName = "Dot";
